// Manual perspective-projection camera (no rendering library dependency).
// Replaces the old fixed 2:1 dimetric isometric projection (isometric.ts)
// with a true 3D camera that orbits the grid and projects world-space
// points through a perspective divide. Grid rotation is now handled
// entirely by orbiting the camera around the grid's center (yaw), instead
// of rotating grid coordinates before projecting them.

export type Vec3 = { x: number; y: number; z: number };
export type Vec2 = { x: number; y: number };

export type Camera = {
  /** Eye position in world space. */
  position: Vec3;
  /** Normalized look direction (world space, points from eye toward the grid center). */
  forward: Vec3;
  /** Normalized camera-right basis vector (world space). */
  right: Vec3;
  /** Normalized camera-up basis vector (world space). */
  up: Vec3;
  /** Perspective strength; larger values narrow the field of view. */
  focalLength: number;
  /** Screen-space point the camera's forward axis projects to. */
  canvasCenter: Vec2;
  /** World-units-to-pixels multiplier, auto-fit to the canvas (replaces computeBoardScale). */
  scale: number;
};

/** Perspective strength. Tunable; larger values narrow the field of view and flatten the look. */
export const FOCAL_LENGTH = 8000;

/** Camera pitch measured from vertical (0 = looking straight down, 90 = looking at the horizon). */
export const CAMERA_PITCH_DEG = 35.26;

/**
 * Multiplies the base distance heuristic (see `createCamera`) to push the
 * camera much farther from the grid while the default focal length grows to
 * compensate for the resulting shrinkage — the classic "telephoto
 * compression" trick. Distance alone doesn't reduce perspective distortion
 * (a fixed-distance focal-length change is pure uniform magnification,
 * since focalLength cancels out of the near/far screen-size ratio); distance
 * and focal length must grow together to flatten the look toward isometric.
 * Calibrated so the near/far tile-size ratio goes from ~0.71 (today's
 * default) to ~0.95 (flat, but still a faint 3D depth cue) — see the
 * iteration 43 grilling session for the full derivation.
 */
const ISOMETRIC_DISTANCE_MULTIPLIER = 8;

/**
 * The smallest scale factor `createCamera` will ever return, purely as a
 * defensive floor against a pathologically tiny or zero-sized canvas (e.g.
 * before the first layout pass) — not a design choice about how small the
 * board should look. Mirrors the old isometric.ts `MIN_BOARD_SCALE`.
 */
const MIN_BOARD_SCALE = 0.05;

/** How much of the smaller canvas dimension the projected grid bounding box should fill. */
const DEFAULT_MARGIN_RATIO = 0.85;

const EPS = 1e-6;

export function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function length(v: Vec3): number {
  return Math.sqrt(dot(v, v));
}

export function normalize(v: Vec3): Vec3 {
  const len = length(v);
  if (len < EPS) return { x: 0, y: 0, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

/**
 * Builds a camera orbiting the grid's center at a fixed pitch, auto-fitting
 * its distance and pixel scale so the whole grid (footprint plus vertical
 * headroom for the tallest possible stack) fills `marginRatio` of the
 * smaller canvas dimension — the perspective analogue of the old
 * `computeBoardScale`/`getOrigin` pair. Defensive against degenerate inputs
 * (zero/negative canvas or grid size): always returns finite values.
 *
 * `focalLength`/`pitchDeg` default to the module constants `FOCAL_LENGTH`/
 * `CAMERA_PITCH_DEG` so existing callers that omit them see identical
 * behavior to before; callers that pass their own values (e.g. the Zoom/
 * Tilt sliders) get a camera fit to that focal length/pitch instead.
 */
export function createCamera(
  canvasWidth: number,
  canvasHeight: number,
  size: number,
  heightLimit: number,
  rotation: number,
  bottomPadding = 0,
  marginRatio = DEFAULT_MARGIN_RATIO,
  focalLength = FOCAL_LENGTH,
  pitchDeg = CAMERA_PITCH_DEG,
): Camera {
  const safeWidth = canvasWidth > 0 ? canvasWidth : 1;
  const safeHeight = canvasHeight > 0 ? canvasHeight : 1;
  const safeSize = size > 0 ? size : 1;
  const safeHeightLimit = heightLimit >= 0 && Number.isFinite(heightLimit) ? heightLimit : 0;
  const safeBottomPadding = Number.isFinite(bottomPadding) ? Math.max(0, bottomPadding) : 0;
  const safeRotation = Number.isFinite(rotation) ? rotation : 0;
  const safeFocalLength = Number.isFinite(focalLength) && focalLength > 0 ? focalLength : FOCAL_LENGTH;
  const safePitchDeg = Number.isFinite(pitchDeg) ? pitchDeg : CAMERA_PITCH_DEG;

  const cx = (safeSize - 1) / 2;
  const cy = (safeSize - 1) / 2;
  const cz = safeHeightLimit / 2;
  const target: Vec3 = { x: cx, y: cy, z: cz };

  const pitchRad = (safePitchDeg * Math.PI) / 180;
  // Distance heuristic: grows with grid footprint and height so the camera
  // backs off for bigger boards/taller stacks instead of clipping through
  // them. Multiplied by ISOMETRIC_DISTANCE_MULTIPLIER (paired with the
  // higher default FOCAL_LENGTH) to flatten the default look toward
  // isometric; see that constant's doc comment.
  const distance = (safeSize * 1.6 + safeHeightLimit * 0.6 + 2) * ISOMETRIC_DISTANCE_MULTIPLIER;

  const basis = orbitBasis(target, distance, pitchRad, safeRotation);

  // `scale`/`canvasCenter` are deliberately computed from a *fixed reference
  // orientation* (rotation = 0) rather than the actual orbit rotation. The
  // camera stays the same distance from the target at every rotation (only
  // azimuth changes), so a square, center-anchored grid's on-screen size is
  // effectively rotation-invariant; reusing one reference fit keeps the grid
  // center and overall scale visually stable while the camera orbits,
  // instead of the board subtly resizing/recentering as it rotates.
  //
  // The fit is *also* deliberately computed at the fixed default
  // `FOCAL_LENGTH`, not `safeFocalLength`. A pure focal-length change,
  // combined with a same-call auto-fit, is otherwise perfectly
  // self-canceling: the projected bounding box grows/shrinks exactly
  // proportionally to focalLength, so solving `scale = canvas / bbox` on
  // every call always yields `scale * focalLength` = the same constant,
  // making the Zoom slider a complete no-op. Calibrating the fit against
  // the fixed default focal length (like the fixed rotation=0 reference)
  // breaks that cancellation: a real render then reintroduces the caller's
  // chosen focal length on top of a *stable* baseline scale, so a narrower
  // FOV genuinely magnifies the grid and flattens its perspective, and a
  // wider FOV genuinely shrinks it and exaggerates foreshortening — a real
  // zoom effect instead of an invisible one.
  const baseCanvasCenter: Vec2 = { x: safeWidth / 2, y: (safeHeight - safeBottomPadding) / 2 };
  const referenceBasis = orbitBasis(target, distance, pitchRad, 0);
  const provisional: Camera = {
    ...referenceBasis,
    focalLength: FOCAL_LENGTH,
    canvasCenter: baseCanvasCenter,
    scale: 1,
  };

  // Measure the projected bounding box (at scale=1) of the grid's world-space
  // AABB — footprint corners at height 0 and the tallest possible stack — to
  // solve for the scale (and center) that fits it within the canvas.
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const x of [-0.5, safeSize - 0.5]) {
    for (const y of [-0.5, safeSize - 0.5]) {
      for (const z of [0, safeHeightLimit]) {
        const p = project(provisional, x, y, z);
        if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
          minX = Math.min(minX, p.x);
          maxX = Math.max(maxX, p.x);
          minY = Math.min(minY, p.y);
          maxY = Math.max(maxY, p.y);
        }
      }
    }
  }

  let scale = MIN_BOARD_SCALE;
  let canvasCenter = baseCanvasCenter;
  if (Number.isFinite(minX) && Number.isFinite(maxX) && Number.isFinite(minY) && Number.isFinite(maxY)) {
    const bboxWidth = Math.max(maxX - minX, EPS);
    const bboxHeight = Math.max(maxY - minY, EPS);
    const rawScale = Math.min(safeWidth / bboxWidth, safeHeight / bboxHeight) * marginRatio;
    scale = Number.isFinite(rawScale) && rawScale > 0 ? Math.max(MIN_BOARD_SCALE, rawScale) : MIN_BOARD_SCALE;

    // Recenter so the projected bbox (not just the forward axis) lands in
    // the middle of the available canvas area. Computed against the fixed
    // reference orientation, so it too stays constant across rotations.
    const offsetX = (minX + maxX) / 2 - baseCanvasCenter.x;
    const offsetY = (minY + maxY) / 2 - baseCanvasCenter.y;
    canvasCenter = {
      x: baseCanvasCenter.x - offsetX * scale,
      y: baseCanvasCenter.y - offsetY * scale,
    };
  }

  return { ...basis, focalLength: safeFocalLength, canvasCenter, scale };
}

/** Computes the eye position and orthonormal look basis for a camera orbiting `target` at `distance`/`pitchRad`/`rotation`. */
function orbitBasis(
  target: Vec3,
  distance: number,
  pitchRad: number,
  rotation: number,
): { position: Vec3; forward: Vec3; right: Vec3; up: Vec3 } {
  const offset: Vec3 = {
    x: distance * Math.sin(pitchRad) * Math.cos(rotation),
    y: distance * Math.sin(pitchRad) * Math.sin(rotation),
    z: distance * Math.cos(pitchRad),
  };
  const position: Vec3 = { x: target.x + offset.x, y: target.y + offset.y, z: target.z + offset.z };

  const forward = normalize(sub(target, position));
  const worldUp: Vec3 = { x: 0, y: 0, z: 1 };
  let right = normalize(cross(forward, worldUp));
  if (length(right) < EPS) {
    // Degenerate only if forward is parallel to worldUp, which the fixed
    // pitch never produces, but guard against it anyway.
    right = { x: 1, y: 0, z: 0 };
  }
  const up = normalize(cross(right, forward));

  return { position, forward, right, up };
}

/** Projects a world-space point to screen space through the camera's perspective divide. */
export function project(camera: Camera, worldX: number, worldY: number, worldZ: number): Vec2 {
  const rel: Vec3 = {
    x: worldX - camera.position.x,
    y: worldY - camera.position.y,
    z: worldZ - camera.position.z,
  };
  const viewX = dot(rel, camera.right);
  const viewY = dot(rel, camera.up);
  const viewDepth = dot(rel, camera.forward);
  const safeDepth = Math.abs(viewDepth) < EPS ? (viewDepth < 0 ? -EPS : EPS) : viewDepth;

  return {
    x: camera.canvasCenter.x + (viewX / safeDepth) * camera.focalLength * camera.scale,
    y: camera.canvasCenter.y - (viewY / safeDepth) * camera.focalLength * camera.scale,
  };
}

/**
 * Casts a ray from the camera through the given screen pixel and intersects
 * it with the horizontal world plane at `planeHeight`, returning the world
 * (x, y) hit point, or null if the ray is parallel to the plane (never
 * intersects). Used for picking: mirrors the old "ignore height, approximate
 * at height 0" behavior, but via a proper 3D ray instead of a linear inverse.
 */
export function unprojectToPlane(
  camera: Camera,
  screenX: number,
  screenY: number,
  planeHeight: number,
): { x: number; y: number } | null {
  const denom = camera.focalLength * camera.scale;
  if (Math.abs(denom) < EPS) return null;

  const ndcX = (screenX - camera.canvasCenter.x) / denom;
  const ndcY = -(screenY - camera.canvasCenter.y) / denom;

  const dir: Vec3 = {
    x: ndcX * camera.right.x + ndcY * camera.up.x + camera.forward.x,
    y: ndcX * camera.right.y + ndcY * camera.up.y + camera.forward.y,
    z: ndcX * camera.right.z + ndcY * camera.up.z + camera.forward.z,
  };

  if (Math.abs(dir.z) < EPS) return null;

  const t = (planeHeight - camera.position.z) / dir.z;
  return {
    x: camera.position.x + t * dir.x,
    y: camera.position.y + t * dir.y,
  };
}

/**
 * Camera-space depth (distance along the forward axis) of a world-space
 * point, used as the painter's-algorithm sort key: draw far-to-near
 * (largest depth first) so nearer blocks correctly occlude farther ones.
 */
export function cameraDepth(camera: Camera, worldX: number, worldY: number, worldZ: number): number {
  const rel: Vec3 = {
    x: worldX - camera.position.x,
    y: worldY - camera.position.y,
    z: worldZ - camera.position.z,
  };
  return dot(rel, camera.forward);
}

/**
 * Outward normals for the 4 side walls of an axis-aligned unit tile, in the
 * same grid-traversal edge order as renderer.ts's `tileTopCorners` (edge
 * 0->1 is the y=y-0.5 wall, 1->2 is x=x+0.5, 2->3 is y=y+0.5, 3->0 is
 * x=x-0.5).
 */
export const TILE_FACE_NORMALS: readonly Vec3[] = [
  { x: 0, y: -1, z: 0 },
  { x: 1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
  { x: -1, y: 0, z: 0 },
];

/**
 * Dot product of a tile side face's outward normal with the direction from
 * the tile toward the camera. Positive means the face geometrically faces
 * the camera (should be drawn); zero or negative means it faces away
 * (should be skipped). Computed in true 3D world space, so — unlike a
 * screen-space winding heuristic derived from projected 2D corners — this
 * stays correct regardless of camera orbit rotation, pitch, or focal length.
 */
export function faceFacing(camera: Camera, x: number, y: number, height: number, faceIndex: number): number {
  const toCamera = normalize(sub(camera.position, { x, y, z: height / 2 }));
  return dot(TILE_FACE_NORMALS[faceIndex], toCamera);
}
